// Admin — Image upload field with preview (avatar or wide banner)
import React, { useRef, useState } from 'react';
import { Upload, X, Loader2, User, Image as ImageIcon } from 'lucide-react';
import { uploadAdminImage, uploadBannerImage, resolveMediaUrl } from '../../api';
import toast from 'react-hot-toast';

interface Props {
  label?: string;
  value?: string;
  onChange: (url: string) => void;
  className?: string;
  /** avatar = round profile; banner = wide promo preview */
  variant?: 'avatar' | 'banner';
  required?: boolean;
  hint?: string;
}

export default function ImageUploadField({
  label = 'Profile Image',
  value,
  onChange,
  className = '',
  variant = 'avatar',
  required = false,
  hint,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const preview = value ? resolveMediaUrl(value) : '';
  const isBanner = variant === 'banner';

  const onFile = async (file?: File | null) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > (isBanner ? 8 : 5) * 1024 * 1024) {
      toast.error(isBanner ? 'Image must be under 8 MB' : 'Image must be under 5 MB');
      return;
    }
    setUploading(true);
    try {
      const res = await (isBanner ? uploadBannerImage(file) : uploadAdminImage(file));
      const url = res.data?.data?.url;
      if (!url) throw new Error('No URL returned');
      onChange(url);
      toast.success('Image uploaded');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div className={className}>
      <label className="block text-xs font-medium text-gray-700 mb-2">
        {label}{required ? ' *' : ''}
      </label>

      {isBanner ? (
        <div className="space-y-3">
          <div className="relative w-full aspect-[16/7] rounded-xl overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200 border border-gray-200">
            {preview ? (
              <img src={preview} alt="Banner preview" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center text-gray-400 gap-2">
                <ImageIcon size={28} />
                <span className="text-xs">Recommended 1200×520 · JPG/PNG/WEBP</span>
              </div>
            )}
            {preview && (
              <button
                type="button"
                title="Remove"
                onClick={() => onChange('')}
                className="absolute top-2 right-2 w-7 h-7 rounded-full bg-red-500 text-white flex items-center justify-center shadow hover:bg-red-600"
              >
                <X size={13} />
              </button>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <input
              ref={inputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              className="hidden"
              onChange={(e) => onFile(e.target.files?.[0])}
            />
            <button
              type="button"
              disabled={uploading}
              onClick={() => inputRef.current?.click()}
              className="btn-secondary btn-sm inline-flex items-center gap-2"
            >
              {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
              {uploading ? 'Uploading…' : preview ? 'Change Image' : 'Upload Banner Image'}
            </button>
            <span className="text-xs text-gray-400">
              {hint || 'Upload a real promo photo · max 8 MB'}
            </span>
          </div>

          <input
            className="input text-xs"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Or paste image URL (https://…)"
          />
        </div>
      ) : (
        <div className="flex items-center gap-4">
          <div className="relative w-20 h-20 flex-shrink-0">
            <div className="w-20 h-20 rounded-full overflow-hidden bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center border-2 border-white shadow">
              {preview ? (
                <img src={preview} alt="Preview" className="w-full h-full object-cover" />
              ) : (
                <User size={28} className="text-white/90" />
              )}
            </div>
            {preview && (
              <button
                type="button"
                title="Remove"
                onClick={() => onChange('')}
                className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center shadow hover:bg-red-600"
              >
                <X size={12} />
              </button>
            )}
          </div>

          <div className="flex-1 min-w-0">
            <input
              ref={inputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              className="hidden"
              onChange={(e) => onFile(e.target.files?.[0])}
            />
            <button
              type="button"
              disabled={uploading}
              onClick={() => inputRef.current?.click()}
              className="btn-secondary btn-sm inline-flex items-center gap-2"
            >
              {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
              {uploading ? 'Uploading…' : preview ? 'Change Image' : 'Upload Image'}
            </button>
            <p className="text-xs text-gray-400 mt-1.5">{hint || 'JPG, PNG, WEBP or GIF · max 5 MB'}</p>
            {value && (
              <input
                className="input mt-2 text-xs"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder="Or paste image URL"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
